import { useQueryClient } from '@tanstack/react-query';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import { useRef, useState } from 'react';
import { useSession } from '../SessionProvider';
import {
  ExportFormat,
  getReportExportQueueStatus,
  getReportExportResult,
  getReportExportResultShipList,
  startQueryingReportExportResult,
  startQueryingReportExportResultShipList,
} from './exportReports';
import { useTimeZoneContext } from '@ais-ui/utils';

interface UseCompleteReportExportFetchingProcessResult {
  startJob: (args: ReportExportJobArgs) => void;
  isLoading: boolean;
  isError: boolean;
  data: string | Array<any> | undefined;
  status: string | undefined;
  currentJobId: string | null;
  currentFormat: ExportFormat | null;
}

interface ReportExportJobArgs {
  jobId: string;
  format: ExportFormat;
  centerLat: number;
  centerLon: number;
  zoomLevel: number;
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

export const useCompleteReportExportFetchingProcess =
  (): UseCompleteReportExportFetchingProcessResult => {
    const { timeZone } = useTimeZoneContext();
    const [queueId, setQueueId] = useState<string>('');
    const [isReportFetched, setIsReportFetched] = useState(false);
    const { dbSession } = useSession();
    const [currentFormat, setCurrentFormat] = useState<ExportFormat | null>(
      null,
    );
    const [currentJobId, setCurrentJobId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const resultCacheRef = useRef(new Map());

    const resetExportData = () => {
      setQueueId('');
      setIsReportFetched(false);
      setCurrentFormat(null);
      setCurrentJobId(null);

      resultCacheRef.current.clear();

      queryClient.removeQueries({ queryKey: ['pollStatusReportExport'] });
      queryClient.removeQueries({ queryKey: ['fetchExportResults'] });
    };

    const startReportExportJob = useMutation(
      {
        mutationFn: async (args: ReportExportJobArgs) => {
          resetExportData();

          const {
            jobId,
            format,
            centerLat,
            centerLon,
            zoomLevel,
            minLat,
            minLon,
            maxLat,
            maxLon,
          } = args;
          setCurrentFormat(format);
          setCurrentJobId(jobId);
          if (!dbSession) {
            throw new Error('dbSession is not defined.');
          }
          if (format === 'OF_SHIPLIST') {
            const queueId = await startQueryingReportExportResultShipList(
              dbSession,
              jobId,
            );
            return queueId;
          } else {
            const queueId = await startQueryingReportExportResult(
              dbSession,
              jobId,
              format,
              centerLat,
              centerLon,
              zoomLevel,
              minLat,
              minLon,
              maxLat,
              maxLon,
              timeZone,
            );
            return queueId;
          }
        },
        onSuccess: (queueId) => {
          setQueueId(queueId);
        },
      },
      'reportExport:start',
    );

    const pollStatus = useQuery(
      {
        queryKey: ['pollStatusReportExport', queueId],
        queryFn: async () => {
          if (!dbSession) {
            throw new Error('dbSession is not defined.');
          }

          const statusData = await getReportExportQueueStatus(
            dbSession,
            queueId,
          );

          // Let React Query handle the error naturally by throwing
          if (statusData?.status === 'QueueFailed') {
            throw new Error('Queue failed');
          }

          return statusData;
        },
        enabled: !!queueId,
        refetchInterval: (query) => {
          const data = query.state.data;
          // Stop polling when complete or failed
          if (
            data?.status === 'QueueComplete' ||
            data?.status === 'QueueFailed'
          ) {
            return false;
          }
          return 1000;
        },
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: false,
        retry: false, // Don't retry on failure for queue status
      },
      'reportExport:pollStatus',
    );

    // Fetch results
    const fetchExportUrl = useQuery(
      {
        queryKey: ['fetchExportResults', queueId],
        queryFn: async () => {
          if (!dbSession) {
            throw new Error('dbSession is not defined.');
          }
          // Check if we've already cached this result
          if (resultCacheRef.current.has(queueId)) {
            return resultCacheRef.current.get(queueId);
          }

          // Fetch the result
          let result;
          if (currentFormat === 'OF_SHIPLIST') {
            result = await getReportExportResultShipList(dbSession, queueId);
          } else {
            result = await getReportExportResult(dbSession, queueId);
          }

          // Cache the result to prevent duplicate fetches
          resultCacheRef.current.set(queueId, result);

          return result;
        },
        enabled:
          pollStatus.data?.status === 'QueueComplete' && !isReportFetched,
      },
      'reportExport:fetchUrl',
    );

    // Function to start the job
    const startJob = (args: ReportExportJobArgs) => {
      setIsReportFetched(false);
      startReportExportJob.mutate(args);
    };

    return {
      startJob,
      isLoading:
        startReportExportJob.isPending ||
        pollStatus.isFetching ||
        (pollStatus.data?.status === 'QueueComplete' &&
          !isReportFetched &&
          fetchExportUrl.isFetching),
      isError:
        startReportExportJob.isError ||
        pollStatus.isError ||
        fetchExportUrl.isError,
      data: fetchExportUrl.data,
      status: pollStatus.data?.status,
      currentJobId,
      currentFormat,
    };
  };
